import {
  clampAmount,
  formatQuantity,
  type Quantity,
  stepQuantity,
  UNIT_LABEL,
  UNITS,
  type Unit,
} from "@larder/shared";
import { useState } from "react";

/**
 * How much of a thing there is.
 *
 * Three ways in, because the right one depends on what you already know:
 *
 *   a SUGGESTION chip, when the name alone implies the answer ("1 L" for milk)
 *   a STEPPER, for the common correction of "half a kilo, not one"
 *   a UNIT select, for the rarer case of the unit itself being wrong
 *
 * "Not set" is a first-class state and the DEFAULT. An empty quantity is
 * honest — plenty of things are bought loose, and enrichment fills the blank
 * moments after saving anyway. Pre-filling "1 piece" would put a number nobody
 * chose into a field that looks authoritative.
 */
export function QuantityField({
  value,
  suggestion,
  onChange,
  idPrefix = "qty",
  addLabel = "+ How much?",
  hideUnits = [],
}: {
  value: Quantity | null;
  /** A free local guess from the name. Offered, never applied silently. */
  suggestion?: Quantity | null;
  onChange: (next: Quantity | null) => void;
  /** Keeps test ids unique when the control appears on two screens. */
  idPrefix?: string;
  /** The empty-state chip, when the surrounding sentence already says "how much". */
  addLabel?: string;
  /**
   * Units to leave out of the picker. The add screen counts packs in its own
   * control, so "pack" as a SIZE there is a second way to say the same thing.
   * A value already in one of these units is still listed — a select must
   * never show a value it does not offer.
   */
  hideUnits?: readonly Unit[];
}) {
  // Only worth offering when there is nothing set — once there is a value, the
  // stepper is a better way to change it than a chip that overwrites it.
  const offer = !value && suggestion ? suggestion : null;

  /**
   * What is in the box, as text, separately from what is saved.
   *
   * The box used to be bound straight to `value.amount`, which meant that the
   * moment you deleted the "1" to type "305" the empty box was rejected and
   * the 1 snapped back — so the only way to enter 305 was to type it after the
   * 1 and then go back for the stray digit. Typing is a sequence of invalid
   * states on the way to a valid one; the field has to let you pass through
   * them. The draft follows the value whenever the value changes from outside
   * (the −/+ buttons, a suggestion chip) and is only pushed back once it reads
   * as a positive number.
   */
  const [draft, setDraft] = useState(value ? String(value.amount) : "");
  // Synced during render, not in an effect: an effect lands a tick after the
  // paint, and a fast tap-then-type can empty the box in that gap only to
  // have the old number put back on top of it.
  const [seen, setSeen] = useState(value?.amount);
  if (value?.amount !== seen) {
    setSeen(value?.amount);
    if (value) setDraft(String(value.amount));
  }

  if (!value) {
    return (
      <div className="qty-empty">
        {offer && (
          <button
            type="button"
            className="chip dashed"
            onClick={() => onChange({ ...offer })}
            data-testid={`${idPrefix}-suggest`}
          >
            {/* The question mark is doing real work: it marks this as a guess
                the app is offering, not a value it has decided. */}
            {formatQuantity(offer)}?
          </button>
        )}
        {/* Reworded when a suggestion is showing. Two chips both reading as
            "set the quantity" made them look like alternatives to each other
            rather than an offer and a way past it — the screenshot of "1 L?"
            beside "+ How much?" is what made that obvious. */}
        <button
          type="button"
          className="chip dashed"
          onClick={() =>
            onChange(suggestion ? { ...suggestion } : { amount: 1, unit: "kg" })
          }
          data-testid={`${idPrefix}-add`}
        >
          {offer ? "Something else" : addLabel}
        </button>
      </div>
    );
  }

  return (
    <div className="qty-row">
      <div className="stepper">
        <button
          type="button"
          className="step"
          onClick={() => onChange(stepQuantity(value, -1))}
          aria-label="Less"
          data-testid={`${idPrefix}-minus`}
        >
          −
        </button>
        <input
          className="step-amount"
          type="number"
          inputMode="decimal"
          min="0"
          step="any"
          value={draft}
          aria-label="Amount"
          onChange={(e) => {
            setDraft(e.target.value);
            const n = Number(e.target.value);
            // An empty or nonsense box must not wipe the unit the user chose —
            // it is mid-edit, not a request to clear the field.
            if (Number.isFinite(n) && n > 0 && e.target.value !== "")
              onChange({ ...value, amount: clampAmount(n) });
          }}
          // Leaving the box empty is not an answer, so the last good number
          // comes back — and a number the clamp trimmed shows what was kept.
          onBlur={() => setDraft(String(value.amount))}
          data-testid={`${idPrefix}-amount`}
        />
        <button
          type="button"
          className="step"
          onClick={() => onChange(stepQuantity(value, 1))}
          aria-label="More"
          data-testid={`${idPrefix}-plus`}
        >
          +
        </button>
      </div>

      {/* A native select, deliberately. Eight units is too many for chips on a
          phone, and the platform picker is the control people already know. */}
      <select
        className="unit-select"
        value={value.unit}
        aria-label="Unit"
        onChange={(e) => onChange({ ...value, unit: e.target.value as Unit })}
        data-testid={`${idPrefix}-unit`}
      >
        {UNITS.filter((u) => u === value.unit || !hideUnits.includes(u)).map((u) => (
          <option key={u} value={u}>
            {UNIT_LABEL[u]}
          </option>
        ))}
      </select>

      <button
        type="button"
        className="ghost"
        onClick={() => onChange(null)}
        data-testid={`${idPrefix}-clear`}
      >
        Clear
      </button>
    </div>
  );
}
